/* ============== OS‑Glass Runtime (Auto-Match + Theme Toggle) ============== */
/* Tries common OS 2.0 color variables; falls back to computed styles.
   Based on Shopify color-system guidance (use schemes, maintain contrast).  */
/* Docs: https://shopify.dev/docs/storefronts/themes/best-practices/design/color-system */
(function(){
  const docEl = document.documentElement;
  const body = document.body;

  // 1) Read a color CSS variable from :root or body (fallback to empty)
  function readVar(name){
    const s1 = getComputedStyle(docEl).getPropertyValue(name).trim();
    if(s1) return s1;
    const s2 = getComputedStyle(body).getPropertyValue(name).trim();
    return s2 || "";
  }

  // 2) Get computed colors if variables are absent
  function computedColor(prop){
    return getComputedStyle(body)[prop] || "";
  }

  // 3) Helpers: parse rgb()/hex, to RGB, luminance & contrast
  function parseColor(input){
    if(!input) return null;
    let s = input.trim().toLowerCase();
    if(s.startsWith("var(")) return null;
    // hex
    if(/^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(s)){
      let hex = s.slice(1);
      if(hex.length===3) hex = hex.split('').map(ch=>ch+ch).join('');
      const r = parseInt(hex.slice(0,2),16), g = parseInt(hex.slice(2,4),16), b = parseInt(hex.slice(4,6),16);
      return {r,g,b};
    }
    // rgb/rgba
    const m = s.match(/rgba?\(([^)]+)\)/);
    if(m){
      const nums = m[1].split(',').map(x=>parseFloat(x));
      return { r: nums[0], g: nums[1], b: nums[2] };
    }
    return null;
  }
  function srgbToLin(c){ c/=255; return (c<=0.04045)? c/12.92 : Math.pow((c+0.055)/1.055, 2.4); }
  function luminance({r,g,b}){
    const R = srgbToLin(r), G = srgbToLin(g), B = srgbToLin(b);
    return 0.2126*R + 0.7152*G + 0.0722*B;
  }
  function contrast(c1, c2){
    const L1 = luminance(c1), L2 = luminance(c2);
    const [a,b] = L1 > L2 ? [L1,L2] : [L2,L1];
    return (a + 0.05) / (b + 0.05);
  }
  function toHex({r,g,b}){
    const h = (n)=>('0'+Math.round(n).toString(16)).slice(-2);
    return `#${h(r)}${h(g)}${h(b)}`;
  }
  function mix(c1, c2, t){ // 0..1
    return {
      r: c1.r + (c2.r-c1.r)*t,
      g: c1.g + (c2.g-c1.g)*t,
      b: c1.b + (c2.b-c1.b)*t
    };
  }

  // 4) Try common variable names used by OS 2.0 themes (Dawn-like)
  //    Not guaranteed across all themes; computed fallback will handle others.
  const varMap = {
    bg:      ["--color-background", "--color-bg", "--bg", "--background", "--color-base-background-1"],
    text:    ["--color-foreground", "--color-text", "--text", "--color-body-text"],
    accent:  ["--color-accent", "--color-primary", "--color-link", "--color-button", "--color-brand"]
  };

  function firstVar(names){
    for(const n of names){
      const v = readVar(n);
      if(v) return v;
    }
    return "";
  }

  // 5) Resolve background, text, accent
  const bgRaw    = firstVar(varMap.bg)   || computedColor('backgroundColor');
  const textRaw  = firstVar(varMap.text) || computedColor('color');
  const accentRaw= firstVar(varMap.accent) || "#e31d2b";

  const bg = parseColor(bgRaw) || {r:14,g:22,b:48};
  const fg = parseColor(textRaw) || {r:233,g:237,b:244};
  const ac = parseColor(accentRaw) || {r:227,g:29,b:43};

  // 6) Derive gradient + muted colors
  // Slightly vary background for start/end and radials
  const bgStart = toHex(mix(bg, {r:0,g:0,b:0}, 0.05));
  const bgEnd   = toHex(mix(bg, {r:0,g:0,b:0}, 0.15));
  const radial1 = toHex(mix(bg, {r:70,g:90,b:160}, 0.20));
  const radial2 = toHex(mix(bg, {r:30,g:80,b:110}, 0.18));

  // Ensure readable text (aim WCAG AA 4.5:1 for body as Shopify recommends)
  // If contrast is low, blend foreground away from bg to increase contrast.
  // https://help.shopify.com/.../accessibility (contrast guidance)
  let bodyText = fg;
  if(contrast(bg, bodyText) < 4.5){
    bodyText = mix(fg, (luminance(bg) > 0.5 ? {r:0,g:0,b:0}:{r:255,g:255,b:255}), 0.25);
  }
  const text     = toHex(bodyText);
  const textStrong = toHex(mix(bodyText, {r:0,g:0,b:0}, 0.25)); // slightly stronger
  const textMuted  = toHex(mix(bodyText, bg, 0.40));            // towards background

  // Accent
  const accent = toHex(ac);

  // 7) Apply variables to :root
  function setVar(n, v){ docEl.style.setProperty(n, v); }
  setVar("--bg-start",  bgStart);
  setVar("--bg-end",    bgEnd);
  setVar("--radial-1",  radial1);
  setVar("--radial-2",  radial2);

  setVar("--text",        text);
  setVar("--text-strong", textStrong);
  setVar("--text-muted",  textMuted);
  setVar("--accent",      accent);

  // Focus ring: favor bluish on light, soft blue on dark
  const isDarkBG = luminance(bg) < 0.35;
  setVar("--focus-ring", isDarkBG ? "#ddecff" : "#2b5fff");

  // 8) Visitor theme toggle (Auto / Light / Dark), persisted
  //    Auto = remove data-theme to allow prefers-color-scheme to apply.
  const KEY = "osg-theme";
  function apply(mode){
    if(mode === "light"){ docEl.setAttribute("data-theme","light"); }
    else if(mode === "dark"){ docEl.setAttribute("data-theme","dark"); }
    else { docEl.removeAttribute("data-theme"); }
  }
  try{
    const saved = localStorage.getItem(KEY);
    apply(saved || "auto");
    document.querySelectorAll("[data-osg]").forEach(btn=>{
      btn.addEventListener("click", ()=>{
        const mode = btn.getAttribute("data-osg");
        localStorage.setItem(KEY, mode);
        apply(mode);
      });
    });
  }catch(e){ /* non-blocking */ }
})();